import { Session } from 'next-auth';

import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { CACHE_TAGS } from '@services/http/http';
import { personServices } from '@services/person';

import {
    SapicCategoryId,
    SapicContextualisedProductV4Payload,
    SapicContextualisedProductV5Payload,
    SapicCustomerCategory,
    SapicFundingMode,
    SapicPriceForFilter,
    SapicPriceForSort,
    SapicProductPayloadV4,
    SapicProductPayloadV5,
    SapicResponseTypeV4,
    SapicResponseTypeV5,
} from '@app-types/api/sapic';
import { QUOTE_TYPES } from '@app-types/cart';
import { FaiType, MobilePlanType } from '@app-types/plan';
import { Product } from '@app-types/product';
import { SimulatorCart } from '@app-types/simulator/virtual-cart';
import { UserType } from '@app-types/user';

import { SapicMapper } from '@helpers/mappers/sapic-mapper';

import { SAPIC_X_VERSION } from '@constants/api';
import { CATEGORY_TYPE, PLAY } from '@constants/provider';

import { z } from 'zod';

export type SapicGetOptionsType = {
    category?: SapicCategoryId;
    categories?: SapicCategoryId[];
    sort?: string;
    offset?: number;
    limit?: number;
    fundingMode?: SapicFundingMode;
    priceFilter?: SapicPriceForFilter;
    priceSort?: SapicPriceForSort;
    plan?: string;
    contract?: string;
    gencodes?: string[];
    withDetails?: boolean;
    filterByTypes?: SapicCategoryId[];
    filterByGencodes?: string[];
    excludeCategoryType?: CATEGORY_TYPE;
    play?: PLAY;
    searchParams?: Record<string, string>;
};

const getProductPayloadApiSchema = z.object({
    panier: z.object({
        parcours: z.array(
            z.object({
                type: z.enum([
                    QUOTE_TYPES.ACQUISITION,
                    QUOTE_TYPES.ACQUISITION_FIXE,
                    QUOTE_TYPES.ACQUISITION_FIXE_V4,
                    QUOTE_TYPES.RENEWAL,
                ]),
                estCourant: z.boolean(),
                produits: z.array(z.object({ gencode: z.string() })),
                idContrat: z.string().optional(),
            }),
        ),
    }),
});

const getProductV5PayloadApiSchema = z.object({
    referenceExterneParcoursCible: z.string(),
    panier: z.object({
        catalogue: z.literal('WEB'),
        acheteur: z
            .object({
                type: z.enum(['PERSONNE', 'NON_CONNU_GP', 'NON_CONNU_PRO']),
                noPersonne: z.string().optional(),
            })
            .optional(),
        parcours: z.array(
            z.object({
                referenceExterne: z.string(),
                typeParcours: z.union([z.literal(QUOTE_TYPES.ACQUISITION), z.literal(QUOTE_TYPES.ACQUISITION_FIXE)]),
                statut: z.union([z.literal('EN_COURS'), z.literal('TERMINE')]),
                elements: z.array(
                    z.object({
                        referenceExterne: z.string(),
                        type: z.enum(['ELEMENT_PANIER', 'LIVRAISON']),
                        composantCommercial: z.object({ identifiant: z.string(), typeCatalogue: z.literal('BYTEL') }),
                    }),
                ),
            }),
        ),
    }),
});

class SapicRepository {
    private readonly STORE = 'web';
    private readonly url: string;

    private defaultGetProductsOptions: SapicGetOptionsType = {
        sort: 'nouveautes',
        offset: 0,
        limit: 12,
        fundingMode: SapicFundingMode.AUTO,
        priceFilter: SapicPriceForFilter.INITIAL,
        priceSort: SapicPriceForSort.INITIAL,
        withDetails: false,
    };

    public constructor(url: string) {
        this.url = url;
    }

    public async getFaiPlans(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
    ): Promise<{ products: FaiType[]; count: number }> {
        return this.contextualisedProducts<SapicResponseTypeV5>(virtualCart, options, session, SAPIC_X_VERSION.V5).then(
            (response) => ({
                products: response.produits.map((product) => SapicMapper.mapToFai(product)),
                count: response.nombreTotalProduits,
            }),
        );
    }

    public async getMobilePlans(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
        apiVersion: SAPIC_X_VERSION = SAPIC_X_VERSION.V5,
    ): Promise<{ products: MobilePlanType[]; count: number }> {
        return this.contextualisedProducts<SapicResponseTypeV4 | SapicResponseTypeV5>(
            virtualCart,
            options,
            session,
            apiVersion,
        )
            .then((response) => ({
                products: response.produits.map((product) => SapicMapper.mapToMobilePlan(product)),
                count: response.nombreTotalProduits,
            }))
            .catch(() => {
                return {
                    products: [],
                    count: 0,
                };
            });
    }

    public async getProducts(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
        xVersion: SAPIC_X_VERSION = SAPIC_X_VERSION.V5,
    ): Promise<{ products: Product[]; count: number }> {
        return this.contextualisedProducts<SapicResponseTypeV5>(virtualCart, options, session, xVersion).then(
            (response) => ({
                products: response.produits.map(
                    (product) => SapicMapper.mapToProduct(product, options.contract !== undefined) as Product,
                ),
                count: response.nombreTotalProduits,
            }),
        );
    }

    private async contextualisedProducts<T>(
        virtualCart: SimulatorCart,
        options: SapicGetOptionsType,
        session: Session | null = null,
        xVersion: SAPIC_X_VERSION = SAPIC_X_VERSION.V4,
    ): Promise<T> {
        const headers: Record<string, string> = {
            'x-version': `${xVersion}`,
            'x-process': 'ConsulterProduitsContextualises',
        };

        const user = session?.user as UserType | undefined;

        let idPu: string | undefined;
        if (user) {
            idPu = await personServices.getIdPuFromContract(
                user,
                options.contract || virtualCart?.cart?.quotes[0]?.contractId,
            );
        }

        try {
            let body;
            let params;
            if (xVersion === SAPIC_X_VERSION.V5) {
                body = getProductV5PayloadApiSchema.parse(this.createSapicV5Payload(virtualCart, idPu));
                params = this.generateSearchParamV5(options, idPu);
            } else {
                body = getProductPayloadApiSchema.parse(this.createSapicPayload(virtualCart, idPu));
                params = this.generateSearchParamV4(options, idPu);
            }

            return await apiHttpService.post<T>(
                `${this.url}/catalogue/produits-contextualises?${new URLSearchParams(params)}`,
                body,
                {
                    headers,
                    user: idPu ? user : undefined,
                    ttl: parseInt(getAppConfig().cache.ttl.products),
                    tags: [CACHE_TAGS.PRODUCTS],
                },
            );
        } catch {
            throw new Error('Could not fetch sapic products');
        }
    }

    private createSapicPayload(virtualCart: SimulatorCart, idPu?: string): SapicContextualisedProductV4Payload {
        const payload: SapicContextualisedProductV4Payload = {
            panier: {
                parcours:
                    virtualCart.cart.quotes?.map((quote) => {
                        const quotePayload: SapicContextualisedProductV4Payload['panier']['parcours'][0] = {
                            estCourant: quote.isCurrent,
                            type: quote.type,
                            produits:
                                quote.products?.map((product) => ({
                                    gencode: product.gencode,
                                })) || [],
                        };

                        if (quote.contractId) {
                            quotePayload.idContrat = quote.contractId;
                        }

                        return quotePayload;
                    }) || [],
            },
        };

        if (idPu) {
            payload.panier.acheteur = {
                type: 'GP',
                noPersonne: idPu,
            };
        }

        return payload;
    }

    private createSapicV5Payload(virtualCart: SimulatorCart, idPu?: string): SapicContextualisedProductV5Payload {
        const payload: SapicContextualisedProductV5Payload = {
            referenceExterneParcoursCible: '',
            panier: {
                catalogue: 'WEB',
                parcours: virtualCart.cart.quotes?.map((quote, quoteIndex) => ({
                    referenceExterne: `idParcours-${quoteIndex}`,
                    typeParcours: quote.type,
                    statut: quote.isCurrent ? 'EN_COURS' : 'TERMINE',
                    elements: quote.products?.map((product, index) => ({
                        referenceExterne: `idElement-${quoteIndex}-${index}`,
                        type: 'ELEMENT_PANIER',
                        composantCommercial: {
                            identifiant: product.gencode,
                            typeCatalogue: 'BYTEL',
                        },
                    })),
                })),
            },
        };

        if (idPu) {
            payload.panier.acheteur = {
                type: 'PERSONNE',
                noPersonne: idPu,
            };
        } else {
            payload.panier.acheteur = {
                type: 'NON_CONNU_GP',
            };
        }

        payload.referenceExterneParcoursCible =
            payload.panier.parcours.find((p) => p.statut === 'EN_COURS')?.referenceExterne || '';
        return payload;
    }

    private generateSearchParamV5(options: SapicGetOptionsType, idPu?: string): SapicProductPayloadV5 {
        const paramsV4 = this.generateSearchParamV4(options, idPu);

        let filter = '';

        if (options.filterByTypes) {
            filter = this.createFilter(options.filterByTypes);
        }

        if (options.play) {
            filter = filter ? `${filter} and play = "${options.play}"` : `play = "${options.play}"`;
        }

        if (options.filterByGencodes) {
            const gencodesFilter = options.filterByGencodes.map((gencode) => `"${gencode}"`).join(',');
            filter = `gencode in (${gencodesFilter})`;
        }

        if (options.excludeCategoryType) {
            filter = filter
                ? `${filter} and typeCategorieFai != "${options.excludeCategoryType}"`
                : `typeCategorieFai != "${options.excludeCategoryType}"`;
        }

        const params: SapicProductPayloadV5 = {
            ...paramsV4,
            ...(options.gencodes && { gencodes: options.gencodes.join(',') }),
        };

        if (filter) {
            params.filtre = filter;
        }

        return params;
    }

    private createFilter(categories: SapicCategoryId[] = []): string {
        let typeFilter = `type = "${categories[0]}"`;

        if (categories.length > 0) {
            const to = categories.map((category) => `"${category}"`).join(',');
            typeFilter = `type in (${to})`;
        }

        return typeFilter;
    }

    private generateSearchParamV4(options: SapicGetOptionsType, idPu?: string): SapicProductPayloadV4 {
        const callbackOptions: SapicGetOptionsType = { ...this.defaultGetProductsOptions, ...options };

        return {
            boutique: this.STORE,
            modePourFinancement: callbackOptions.fundingMode,
            prixReferencePourFiltre: callbackOptions.priceFilter,
            tri: callbackOptions.sort,
            detail: callbackOptions?.withDetails ? '1' : '0',
            limite: callbackOptions.limit?.toString(),
            decalage: callbackOptions.offset?.toString(),
            prixReferencePourTri: callbackOptions.priceSort,
            ...(callbackOptions.category && { categorie: callbackOptions.category }),
            ...(callbackOptions.categories && { categorie: callbackOptions.categories.join(',') }),
            ...(callbackOptions.gencodes && { gencodes: callbackOptions.gencodes }),
            // SAPIC Automatically search the right clientCategorie if idPu is provided
            ...(idPu ? { idPu } : { clientCategorie: SapicCustomerCategory.GP }),
            ...options.searchParams,
        } as SapicProductPayloadV4;
    }
}

export const sapicRepository = new SapicRepository(getAppConfig().b2r.url);
